import React from 'react';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';
import cx from 'classnames';
import PropTypes from 'prop-types';
import { DateTime } from 'luxon';

import withStyles from '@mui/styles/withStyles';
import Paper from '@mui/material/Paper';

import { updateFirm } from '../store/slices/firmsSlice';
import { updateVisibleFirm } from '../store/slices/firmInfoSlice';
import { clearTaskAccomplishmentInfo } from '../store/slices/dashboardStatsSlice';
import { setApiResponseInfo } from '../store/slices/apiResponseInfoSlice';

import {
  createStdRequest,
  apiFetch,
  calculatePhoneNumberChange,
  validatePhoneNumber,
  saveWebsiteLinkToDb,
  deleteWebsiteLinkFromDb,
  validateAddress,
  isNonLocalNonLoggedInVisitor,
  showAlert,
  logWithTime,
  sortedDropdownItemLabels,
} from '../utils/utils';

import { outreachRepForDisplay, noteRepForDisplayAsActivity, sortIntoActivityGroups } from '../utils/activityProcessor';

import ReduxLoader from '../store/ReduxLoader';
import DA from '../store/ReduxAccess';
import SocketAccess from '../comm/SocketAccess';

import ActivitySummaryBox from '../components/ActivitySummaryBox';
import ActionPanel from '../components/ActionPanel';
import ActivityList from '../components/ActivityList';
import ContactAssocGroup from '../components/ContactAssocGroup';
import NoteAssocGroup from '../components/NoteAssocGroup';
import ReferralAssocGroup from '../components/ReferralAssocGroup';
import TaskAssocGroup from '../components/TaskAssocGroup';
import ClickAwayTextField from '../components/ClickAwayTextField';
import OrgPropertyEditor from '../components/OrgPropertyEditor';
import BusinessTypeWidget from '../components/BusinessTypeWidget';
import FacePageAddressPanel from '../components/FacePageAddressPanel';
import WebLinkEditor from '../components/WebLinkEditor';
import KSpinner from '../components/KSpinner';
import KSelect from '../components/KSelect';
import AlertModal from '../components/AlertModal';
import CrmSnackbar from '../components/CrmSnackbar';

import facePageStyles from '../assets/jss/crm/views/facePageStyles';

const referrerOrgPageStyles = (theme) => ({
  ...facePageStyles(theme),
  radioButton: {
    padding: '8px 3px 8px 12px',
    '&>span>svg': {
      fontSize: '18px',
    },
  },
  businessTypeLabel: {
    marginBottom: '-4px',
  },
  radioDiv: {
    height: '30px',
    cursor: 'pointer',
  },
});

class ReferrerOrgPage extends React.Component {
  state = {
    loading: true,
    referrerOrg: {
      firmId: 0,
      name: '',
      officePhone: '',
      description: '',
      address: {
        street: '',
        city: '',
        state: '',
        zip: '',
      },
      firmType: 'Treatment Center',
    },
    activityGroups: undefined,
    contacts: [],
    nActivities: 0,
    snackbar: {
      isOpen: false,
      message: '',
    },
  };

  newPhoneObject = null;

  addressValidationPolicy = {
    requireAddress: false,
    forbidPartialAddress: true,
    allowZipFreeAddress: true,
  };

  possibleTemperatures = sortedDropdownItemLabels('Referral Company Temperature');

  componentDidMount() {
    this.registerRefreshCallbacks();
    this.loadRequiredData();
  }

  registerRefreshCallbacks() {
    const outreachCallbackStruct = {
      kind: 'outreach',
      pageName: 'ReferralCompanyPage',
      refreshFunc: () => this.redisplayPageForChangedOutreach(),
    };
    SocketAccess.registerRefreshCallback(outreachCallbackStruct);
  }

  redisplayPageForChangedOutreach() {
    logWithTime('In ReferrerOrgPage calling buildActivityGroupsFromReduxStore() in hopes of showing new call');
    this.buildActivityGroupsFromReduxStore(this.props.referrerOrgId);
  }

  componentWillUnmount() {
    SocketAccess.unregisterRefreshCallbacksForPage('ReferralCompanyPage');
  }

  loadRequiredData() {
    const { referrerOrgId } = this.props;
    const completionCallback = () => {
      this.setReferrerOrg(referrerOrgId);
      this.buildActivityGroupsFromReduxStore(referrerOrgId);
      this.setState({ loading: false });
    };
    ReduxLoader.loadInfoForReferrerOrg(this, referrerOrgId, completionCallback);
  }

  buildActivityGroupsFromReduxStore(visibleFirmId) {
    const outreaches = DA.getOutreachesRelatedToVisibleFirm(visibleFirmId);
    const augmentedOutreaches = outreaches.map((outreach) => outreachRepForDisplay(outreach));
    const notes = this.getNotesAboutFirm();
    const augmentedNotes = notes.map((note) => noteRepForDisplayAsActivity(note));
    const outreachesAndNotes = augmentedOutreaches.concat(augmentedNotes);
    const activityGroups = sortIntoActivityGroups(outreachesAndNotes);
    this.setState({ activityGroups, nActivities: outreachesAndNotes.length }, () => this.setStateContacts());
  }

  setReferrerOrg(referrerOrgId) {
    if (referrerOrgId === 0) {
      const org = {
        firmId: referrerOrgId,
        name: 'Clear Vision Recovery',
        officePhone: '(813) 501-6738',
        description: `Overview: Located in the beautiful sun coast region of Florida, Clean Recovery Centers provide Intensive Outpatient and Outpatient Services, as well as Transitional Living for the treatment of addiction.

Approx. Number of Employees: 30

Founded: 2015`,
        temperature: 'Unknown',
        address: {
          street: '',
          city: 'Clearwater',
          state: 'FL',
          zip: '',
        },
        firmType: 'treatmentCenter',
      };
      this.setState({ referrerOrg: org });
    } else {
      const org = DA.getFirmById(referrerOrgId);
      let dbAddress = org.address;
      if (!dbAddress || dbAddress.addressId === 0) {
        const facilities = org.facilities;
        const facility = facilities && facilities.length > 0 ? facilities[0] : {};
        dbAddress = facility.facilityAddress || {};
      }
      const pageAddress = {
        addressId: dbAddress.addressId,
        street: dbAddress.line1 ? dbAddress.line1 : '',
        city: dbAddress.city ? dbAddress.city : '',
        state: dbAddress.state ? dbAddress.state : '',
        zip: dbAddress.zip ? dbAddress.zip : '',
      };
      const description = org.description || '';
      let officePhone = org.officePhone;
      if (!officePhone && org.facilities && org.facilities.length > 0) {
        const facility = org.facilities[0];
        if (facility.phoneNumbers && facility.phoneNumbers.length > 0) {
          const phoneNumber = facility.phoneNumbers[0];
          officePhone = phoneNumber.number;
        }
      }
      const pageOrg = { ...org, address: pageAddress, description, officePhone };
      this.setState({ referrerOrg: pageOrg });
    }
  }

  showOrgContactFaceSheet(subjectId) {
    showAlert(this, 'Should now show the face sheet for org contact with ID: ' + subjectId);
  }

  functionToShowSubject(activity) {
    switch (activity.subjectType) {
      case 'orgContact':
        return () => this.showOrgContactFaceSheet(activity.subjectId);
      case 'org':
        return () => showAlert(this, "You're already on the page for this organization");
      default:
        return () => showAlert(this, "Don't know how to show subject of type: " + activity.subjectType);
    }
  }

  getContactsAtReferrerOrg() {
    const visibleFirmId = this.props.referrerOrgId;
    return DA.getContactsAtVisibleFirm(visibleFirmId);
  }

  getNotesAboutFirm() {
    return DA.getNotesAboutFirmWithId(this.props.referrerOrgId);
  }

  getReferralsFromFirm(visibleFirmId) {
    return DA.getReferralsFromFirm(visibleFirmId);
  }

  handleOrgOfficePhoneChange(newValue) {
    const oldValue = this.state.referrerOrg.officePhone;
    const phChange = calculatePhoneNumberChange(oldValue, newValue);
    if (phChange.shouldUpdate) {
      const newOrgState = { ...this.state.referrerOrg, officePhone: phChange.newValue };
      this.setState({ referrerOrg: newOrgState });
    }
  }

  handleOrgDescriptionChange(newValue) {
    const newOrgState = { ...this.state.referrerOrg, description: newValue };
    this.setState({ referrerOrg: newOrgState });
  }

  handleOrgStreetChange(newValue) {
    const newAddr = { ...this.state.referrerOrg.address, street: newValue };
    const newOrgState = { ...this.state.referrerOrg, address: newAddr };
    this.setState({ referrerOrg: newOrgState });
  }

  handleOrgCityChange(newValue) {
    const newAddr = { ...this.state.referrerOrg.address, city: newValue };
    const newOrgState = { ...this.state.referrerOrg, address: newAddr };
    this.setState({ referrerOrg: newOrgState });
  }

  handleOrgStateChange(newValue) {
    const newAddr = { ...this.state.referrerOrg.address, state: newValue };
    const newOrgState = { ...this.state.referrerOrg, address: newAddr };
    this.setState({ referrerOrg: newOrgState });
  }

  handleOrgZipChange(newValue) {
    const newAddr = { ...this.state.referrerOrg.address, zip: newValue };
    const newOrgState = { ...this.state.referrerOrg, address: newAddr };
    this.setState({ referrerOrg: newOrgState });
  }

  handleBusinessTypeChange(event, newValue) {
    const newOrgState = { ...this.state.referrerOrg, firmType: newValue };
    this.setState({ referrerOrg: newOrgState }, () => this.saveFirmTypeToDb());
  }

  handleClickOutsideName(newValue) {
    const changed = newValue !== this.state.referrerOrg.name;
    if (changed) {
      const newOrgState = { ...this.state.referrerOrg, name: newValue };
      this.setState({ referrerOrg: newOrgState }, () => this.saveNameToDb());
    }
  }

  saveNameToDb() {
    this.saveOrgStateFieldToDb('name', 'Name');
  }

  /*
      const newOrgState = {...this.state.referrerOrg, officePhone: newValue};
      this.setState({referrerOrg: newOrgState});
   */

  handleClickOutsidePhone(newValue) {
    const changed = newValue !== this.state.referrerOrg.officePhone;
    if (changed) {
      const newOrgState = { ...this.state.referrerOrg, officePhone: newValue };
      this.setState({ referrerOrg: newOrgState });
      const saved = this.savePhoneAsTempObject();
      if (saved) {
        this.saveAddressOrPhoneToDb('Office phone number');
      }
    } else {
      this.newPhoneObject = null;
    }
  }

  savePhoneAsTempObject() {
    const proposedNumber = this.state.referrerOrg.officePhone;
    if (proposedNumber) {
      const validation = validatePhoneNumber(proposedNumber);
      if (!validation.valid) {
        showAlert(this, validation.errorMessage);
        return false;
      }
    }
    if (proposedNumber.length === 0) {
      this.newPhoneObject = {};
    } else {
      // We have a valid phone number and must either modify the previous one or add this new one
      this.newPhoneObject = {
        roles: 'Business',
        countryCode: '1',
        number: proposedNumber,
      };
    }
    return true;
  }

  saveFirmTypeToDb() {
    this.saveOrgStateFieldToDb('firmType', 'Business type');
  }

  saveOrgStateFieldToDb(orgStateFieldName, message) {
    // build API struct
    const orgApiStruct = this.buildApiStructForUpdatingReferrerOrg();
    // const uri = `/api/firm/${viewingFirmId}/visibleFirm`;
    const uri = '/api/firm';
    const request = createStdRequest(uri, 'post', orgApiStruct);
    const actionOnGoodResponse = (json) => {
      const reduxRep = DA.getFirmById(this.state.referrerOrg.firmId);
      const reduxFieldNames = this.reduxFieldNamesForOrgStateName(orgStateFieldName);
      let copiedFirm = reduxRep;
      reduxFieldNames.forEach(
        (reduxFieldName) => (copiedFirm = { ...copiedFirm, [reduxFieldName]: orgApiStruct[reduxFieldName] })
      );
      this.props.updateFirm(copiedFirm);
      this.showSnackbar(message);
    };
    // call API
    apiFetch(this, request, actionOnGoodResponse);
  }

  reduxFieldNamesForOrgStateName(orgStateFieldName) {
    switch (orgStateFieldName) {
      default:
        return [orgStateFieldName];
    }
  }

  buildApiStructForUpdatingReferrerOrg() {
    /*
        public int FirmId { get; set; }
        public string Name { get; set; }
        public string FirmType { get; set; }
        public string Description { get; set; }
        public string Temperature { get; set; }
        public string HomePageUrl { get; set; }
        public string FacebookUrl { get; set; }
        public List<FirmContactApiStruct> Contacts { get; set; } = new List<FirmContactApiStruct>();
        public List<FacilityApiStruct> Facilities { get; set; } = new List<FacilityApiStruct>();
  */
    /*
  state = {
    referrerOrg: {
        firmId: 0,
        name: "",
        officePhone: "",
        description: "",
        temperature: "Unknown",
        address: {
          street: "",
          city: "",
          state: "",
          zip: "",
        },
        firmType: "Treatment Center",
    },
  */
    const { name, firmId, firmType, description, temperature } = this.state.referrerOrg;
    return {
      firmId,
      name,
      firmType,
      contacts: null,
      facilities: null,
      description,
      temperature,
    };
  }

  handleClickOutsideDescription(changed) {
    if (changed) {
      this.saveDescriptionToDb();
    }
  }

  saveDescriptionToDb() {
    // build API struct
    const f2fvApiStruct = this.buildApiStructForUpdatingDescription();
    const viewingFirmId = DA.getFirmId();
    const uri = `/api/firm/${viewingFirmId}/visibleFirm`;
    const request = createStdRequest(uri, 'post', f2fvApiStruct);
    const actionOnGoodResponse = (json) => {
      const reduxRep = DA.getFirmById(this.state.referrerOrg.firmId);
      const copiedFirm = { ...reduxRep, description: this.state.referrerOrg.description };
      this.props.updateFirm(copiedFirm);
    };
    // call API
    apiFetch(this, request, actionOnGoodResponse);
  }

  buildApiStructForUpdatingDescription() {
    /* goal:
        public int Firm2FirmVisibilityId { get; set; }
        public int ViewingFirmId { get; set; }
        public int VisibleFirmId { get; set; }
        public string Description { get; set; }
        public string Temperature { get; set; }
  */
    /*
  state = {
    referrerOrg: {
        firmId: 0,
        name: "",
        officePhone: "",
        description: "",
        temperature: '',
        address: {
          street: "",
          city: "",
          state: "",
          zip: "",
        },
        firmType: "Treatment Center",
    },
  */
    const { firmId, description, temperature } = this.state.referrerOrg;

    return {
      viewingFirmId: DA.getFirmId(),
      visibleFirmId: firmId,
      description,
      temperature,
    };
  }

  handlePossibleAddressChange(changed) {
    if (changed) {
      const validation = this.validateAddress();
      if (validation.valid) {
        this.saveAddressOrPhoneToDb('Address');
      } else {
        showAlert(this, validation.errorMessage);
      }
    }
  }

  validateAddress() {
    const { street, city, state, zip } = this.state.referrerOrg.address;
    const { requireAddress, forbidPartialAddress, allowZipFreeAddress } = this.addressValidationPolicy;
    return validateAddress(street, '', city, state, zip, requireAddress, forbidPartialAddress, allowZipFreeAddress);
  }

  saveAddressOrPhoneToDb(message) {
    // build API struct
    const orgApiStruct = this.buildApiStructForUpdatingReferrerOrgIncludingFacilities();
    const uri = '/api/firm';
    const request = createStdRequest(uri, 'post', orgApiStruct);
    const actionOnGoodResponse = (json) => {
      const reduxRep = DA.getFirmById(this.state.referrerOrg.firmId);
      let copiedFirm = { ...reduxRep, facilities: json.facilities };
      if (this.havePhoneNumberAtFirstFacility(copiedFirm.facilities)) {
        const officePhone = copiedFirm.facilities[0].phoneNumbers[0].number;
        copiedFirm = { ...copiedFirm, officePhone };
      }
      if (this.haveAddressAtFirstFacility(copiedFirm.facilities)) {
        const address = copiedFirm.facilities[0].facilityAddress;
        copiedFirm = { ...copiedFirm, address };
      }
      this.props.updateFirm(copiedFirm);
      this.showSnackbar(message);
    };
    // call API
    apiFetch(this, request, actionOnGoodResponse);
  }

  havePhoneNumberAtFirstFacility(facilities) {
    if (!facilities || facilities.length === 0) {
      return false;
    }
    const phones = facilities[0].phoneNumbers;
    return phones && phones.length > 0;
  }

  haveAddressAtFirstFacility(facilities) {
    if (!facilities || facilities.length === 0) {
      return false;
    }
    return facilities[0].facilityAddress;
  }

  buildApiStructForUpdatingReferrerOrgIncludingFacilities() {
    /*
        public int FirmId { get; set; }
        public string Name { get; set; }
        public string FirmType { get; set; }
        public string Description { get; set; }
        public string HomePageUrl { get; set; }
        public string FacebookUrl { get; set; }
        public List<FirmContactApiStruct> Contacts { get; set; } = new List<FirmContactApiStruct>();
        public List<FacilityApiStruct> Facilities { get; set; } = new List<FacilityApiStruct>();
  */
    /*
  state = {
    referrerOrg: {
        firmId: 0,
        name: "",
        officePhone: "",
        description: "",
        address: {
          street: "",
          city: "",
          state: "",
          zip: "",
        },
        firmType: "Treatment Center",
    },
  */
    const firmId = this.state.referrerOrg.firmId;
    const name = this.state.referrerOrg.name;
    const phoneNumbers = this.createPhonesArrayFromState();
    this.newPhoneObject = null;
    const address = this.createAddressFromState();
    const reduxFacilities = DA.getFacilitiesForFirm(firmId);
    let amendedFacilities = undefined;
    if (reduxFacilities.length > 0) {
      const facilitiesHead = reduxFacilities[0];
      const facilitiesTail = reduxFacilities.slice(1);
      // copy phone number ID to modify DB entries, rather than creating new ones
      const oldPhoneNumbers = facilitiesHead.phoneNumbers;
      if (oldPhoneNumbers && oldPhoneNumbers.length > 0) {
        if (phoneNumbers && phoneNumbers.length > 0) {
          const phoneId = oldPhoneNumbers[0].phoneId;
          phoneNumbers[0].phoneId = phoneId;
        }
      }
      const newFacilitiesHead = { ...facilitiesHead, phoneNumbers, facilityAddress: address };
      amendedFacilities = [newFacilitiesHead].concat(facilitiesTail);
    } else {
      amendedFacilities = [
        {
          facilityId: 0,
          name: 'Sole Facility',
          firmId,
          phoneNumbers,
          facilityAddress: address,
        },
      ];
    }
    return {
      firmId,
      name,
      firmType: this.state.referrerOrg.firmType,
      contacts: null,
      facilities: amendedFacilities,
      description: this.state.referrerOrg.description,
    };
  }

  createPhonesArrayFromState() {
    if (this.newPhoneObject) {
      if (!this.newPhoneObject.hasOwnProperty('number')) {
        return [];
      } else {
        return [this.newPhoneObject];
      }
    } else {
      const number = this.state.referrerOrg.officePhone;
      if (number) {
        const phoneNumberStruct = { roles: 'Business', countryCode: '1', number: number };
        return [phoneNumberStruct];
      } else {
        return [];
      }
    }
  }

  createAddressFromState() {
    const stateAddress = this.state.referrerOrg.address;
    return {
      addressId: stateAddress.addressId,
      roles: 'Business',
      isIntlStr: 'false',
      line1: stateAddress.street,
      line2: '',
      city: stateAddress.city,
      state: stateAddress.state,
      zip: stateAddress.zip,
    };
  }

  updateUI() {
    this.setStateContacts();
  }

  setStateContacts() {
    const contacts = this.getContactsAtReferrerOrg();
    this.setState({ contacts });
  }

  getPossibleTaskSubjects() {
    return this.state.contacts;
  }

  getTaskSubjectCategory() {
    return 'refContact';
  }

  createNoteApiStruct(noteText) {
    const now = DateTime.local();
    const authorEmail = this.props.userInfo.email;
    const viewingFirmId = DA.getFirmId();
    const referrerOrgId = this.state.referrerOrg.firmId;
    return {
      date: now.toISO(),
      authorEmail,
      viewingFirmId,
      otherFirmId: referrerOrgId,
      content: noteText,
    };
  }

  createNote(noteText) {
    const uri = '/api/note';
    const apiStruct = this.createNoteApiStruct(noteText);
    const request = createStdRequest(uri, 'POST', apiStruct);
    const actionOnGoodResponse = (json) => {
      // update Redux store
      this.props.clearTaskAccomplishmentInfo();
      ReduxLoader.addOrUpdateNotesOnFirms([json]);
      const referrerOrgId = this.state.referrerOrg.firmId;
      this.buildActivityGroupsFromReduxStore(referrerOrgId);
    };
    apiFetch(this, request, actionOnGoodResponse);
  }

  saveFirmTypeChangeToDb(firmId, newType) {
    const firm = DA.getFirmById(firmId);
    const modifiedFirm = { ...firm, firmType: newType };
    const uri = '/api/firm';
    const request = createStdRequest(uri, 'post', modifiedFirm);
    const actionOnGoodResponse = (json) => {
      this.props.updateFirm(modifiedFirm);
      const prevFirmInfo = DA.getVisibleFirmInfo(firmId);
      const newFirmInfo = { ...prevFirmInfo, firmId: prevFirmInfo.id, firmType: newType };
      this.props.updateVisibleFirm(newFirmInfo);
      this.showSnackbar('Business type');
    };
    // call API
    apiFetch(this, request, actionOnGoodResponse);
  }

  saveTemperatureChangeToDb(firmId, newTemperature) {
    const firm = DA.getFirmById(firmId);
    const modifiedFirm = { ...firm, temperature: newTemperature };
    const uri = '/api/firm';
    const request = createStdRequest(uri, 'post', modifiedFirm);
    const actionOnGoodResponse = (json) => {
      this.props.updateFirm(modifiedFirm);
      const prevFirmInfo = DA.getVisibleFirmInfo(firmId);
      const newFirmInfo = { ...prevFirmInfo, firmId: prevFirmInfo.id, temperature: newTemperature };
      this.props.updateVisibleFirm(newFirmInfo);
      this.setReferrerOrg(firmId);
      this.showSnackbar('Temperature');
    };
    apiFetch(this, request, actionOnGoodResponse);
  }

  showSnackbar(message) {
    this.setState({
      snackbar: {
        message: message + ' saved',
        isOpen: true,
      },
    });
  }

  render() {
    if (isNonLocalNonLoggedInVisitor()) {
      return <Redirect to="/homeDispatcher" />;
    }

    if (this.state.redirect) {
      return <Redirect to={this.state.redirect} />;
    }

    if (this.state.loading) {
      return <KSpinner />;
    }

    const { classes } = this.props;
    const viewingFirmId = DA.getFirmId();
    const visibleFirmId = this.state.referrerOrg ? this.state.referrerOrg.firmId : 0;
    const allRelatedTasks = DA.getTasksRelatedToVisibleFirm(visibleFirmId);
    const haveActivities =
      this.state.activityGroups && this.state.activityGroups[0] && this.state.activityGroups[0].activities[0];
    const lastInteractionTime = haveActivities ? this.state.activityGroups[0].activities[0].dateTime : 0;
    const contacts = this.state.contacts;
    const shouldShowPhoneNumberEditor = this.state.referrerOrg.firmType !== 'Private Practitioner';
    const webLinks = this.state.referrerOrg.webLinks ? this.state.referrerOrg.webLinks : [];
    const websiteLink = webLinks.find((wl) => wl.linkType === 'Website');
    const websiteUrl = websiteLink ? websiteLink.url : '';

    return (
      <div>
        <AlertModal show={this.state.shouldShowAlertModal} renderer={this} text={this.state.alertModalText} />
        <CrmSnackbar renderer={this} options={this.state.snackbar} />

        <Paper className={classes.root}>
          <div className={classes.columnHolder}>
            <div className={classes.divLeftColumn}>
              <div className={cx(classes.entityProfileFrame, classes.entityProfileFrame_organization)}>
                <div className={classes.entityProfileFrame_headerBox}>
                  {false && (
                    <div className={classes.entityProfileFrame_headerActionsBox}>
                      <div className={classes.entityProfileFrame_headerRightActions}>
                        <span className={classes.entityProfileFrame_headerActionButton} />
                        <span className={classes.entityProfileFrame_headerActionButton} />
                      </div>
                      <div className={classes.u_clearFloat} />
                    </div>
                  )}
                  <div className={classes.entityProfileFrame_titleBoxContainer}>
                    <div className={classes.entityProfileFrame_titleBox}>
                      {false && (
                        <div className={classes.entityProfileFrame_titleIcon}>
                          <div className={cx(classes.profileImageComponent, classes.profileImageComponent_circle)}>
                            <div className={classes.profileImageComponent_image}>
                              <img
                                src={
                                  'https://ali-userassets-production.s3.amazonaws.com/public/company_logos/c/l/e/cleanrecoverycenters.com/logo.png'
                                }
                                alt="Logo"
                              />
                            </div>
                            <div className={classes.profileImageComponent_overlay} />
                          </div>
                        </div>
                      )}
                      <div className={classes.entityProfileFrame_titleBox_text}>
                        <div className={cx(classes.entityProfileFrame_title, classes.u_ellipsis)}>
                          {this.state.referrerOrg.name}
                        </div>
                        <div className={cx(classes.entityProfileFrame_subtitle, classes.u_ellipsis)}>
                          {contacts.length} Contact{contacts.length !== 1 && 's'}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className={classes.entityProfileFrame_contentBox}>
                  <div className={cx(classes.entityDetails, classes.js_scrollable)}>
                    <div className={cx(classes.formFramework_container, classes.formFramework_profileContainer)}>
                      <div className={cx(classes.formFramework_layout, classes.formFramework_listLayout)}>
                        <div className={classes.formFramework_listRow}>
                          <div
                            className={cx(
                              classes.formFramework_wrapper,
                              classes.formFramework_editWrapper,
                              'isRequired'
                            )}
                          >
                            <ClickAwayTextField
                              label="Name"
                              initialText={this.state.referrerOrg.name}
                              required
                              outsideClickHandler={(newValue) => this.handleClickOutsideName(newValue)}
                            />
                          </div>
                        </div>

                        {shouldShowPhoneNumberEditor && (
                          <div className={classes.formFramework_listRow}>
                            <div className={cx(classes.formFramework_wrapper, classes.formFramework_editWrapper)}>
                              <ClickAwayTextField
                                label="Office Phone"
                                initialText={this.state.referrerOrg.officePhone || ''}
                                isPhoneField
                                onChange={(newValue) => this.handleOrgOfficePhoneChange(newValue)}
                                outsideClickHandler={(newValue) => this.handleClickOutsidePhone(newValue)}
                              />
                            </div>
                          </div>
                        )}

                        <div className={classes.formFramework_listRow}>
                          <div className={cx(classes.formFramework_wrapper, classes.formFramework_editWrapper)}>
                            <WebLinkEditor
                              label="Website"
                              linkBoxWidth="186px"
                              initialValue={websiteUrl}
                              clickawayShouldSave={true}
                              onDelete={() =>
                                deleteWebsiteLinkFromDb(this, viewingFirmId, visibleFirmId, this.props.updateFirm, () =>
                                  this.showSnackbar('Website')
                                )
                              }
                              onNewValue={(newText) =>
                                saveWebsiteLinkToDb(
                                  this,
                                  viewingFirmId,
                                  visibleFirmId,
                                  this.props.updateFirm,
                                  newText.trim(),
                                  () => this.showSnackbar('Website')
                                )
                              }
                            />
                          </div>
                        </div>

                        <div className={classes.formFramework_listRow}>
                          <div className={cx(classes.formFramework_wrapper, classes.formFramework_editWrapper)}>
                            <OrgPropertyEditor
                              label="Description"
                              propertyToken="description"
                              visibleFirmId={visibleFirmId}
                              value={this.state.referrerOrg.description}
                              required={false}
                              multiline={true}
                            />
                          </div>
                        </div>

                        <div className={classes.formFramework_listRow}>
                          <div className={cx(classes.formFramework_wrapper, classes.formFramework_editWrapper)}>
                            <div className={classes.formFramework_label}>Address</div>
                            <div className={classes.formFramework_content}>
                              <FacePageAddressPanel
                                onClickAway={(changed) => this.handlePossibleAddressChange(changed)}
                                address={this.state.referrerOrg.address}
                                onStreetChange={(newValue) => this.handleOrgStreetChange(newValue)}
                                onCityChange={(newValue) => this.handleOrgCityChange(newValue)}
                                onStateChange={(newValue) => this.handleOrgStateChange(newValue)}
                                onZipChange={(newValue) => this.handleOrgZipChange(newValue)}
                              />
                            </div>
                          </div>
                        </div>

                        <div className={classes.formFramework_listRow}>
                          <div className={cx(classes.formFramework_wrapper, classes.formFramework_editWrapper)}>
                            <div className={classes.formFramework_content}>
                              <div className={cx(classes.formFramework_item, classes.formFramework_businessTypeItem)}>
                                <BusinessTypeWidget
                                  visibleFirmId={visibleFirmId}
                                  onChange={(newFirmType) => this.saveFirmTypeChangeToDb(visibleFirmId, newFirmType)}
                                />
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className={classes.formFramework_listRow}>
                          <div className={cx(classes.formFramework_wrapper, classes.formFramework_editWrapper)}>
                            <div className={classes.formFramework_content}>
                              <div className={cx(classes.formFramework_item, classes.formFramework_temperatureItem)}>
                                <KSelect
                                  id="temperatureDropdown"
                                  name="temperature"
                                  fullWidth={true}
                                  label="Temperature"
                                  liveOptions={this.possibleTemperatures}
                                  value={this.state.referrerOrg.temperature}
                                  onChange={(event) =>
                                    this.saveTemperatureChangeToDb(visibleFirmId, event.target.value)
                                  }
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className={classes.divRightColumn}>
              <div className={classes.sideColumnStructure}>
                <div className={classes.divMysteryDiv}>
                  <ContactAssocGroup
                    contactsFunc={() => this.getContactsAtReferrerOrg()}
                    viewingFirmId={DA.getFirmId()}
                    referrerOrgId={this.props.referrerOrgId}
                    updateFunc={() => this.updateUI()}
                    contactType="firm"
                  />
                  {false && <NoteAssocGroup notes={this.getNotesAboutFirm()} />}
                  <ReferralAssocGroup referrals={this.getReferralsFromFirm(visibleFirmId)} />
                  <TaskAssocGroup
                    taskSubjectProvider={this}
                    visibleFirmId={visibleFirmId}
                    allRelevantTasks={allRelatedTasks}
                    refreshFund={() => this.setState({ loading: false })}
                  />
                </div>
              </div>
            </div>
            <div className={classes.divCenterColumn}>
              <div className={classes.activityColumn}>
                <ActivitySummaryBox nInteractions={this.state.nActivities} lastInteractionTime={lastInteractionTime} />
                <ActionPanel
                  page={this}
                  activityColumn={this}
                  visibleFirmId={visibleFirmId}
                  contacts={contacts}
                  noteCreationFunc={(noteText) => this.createNote(noteText)}
                />

                {this.state.loadingInteractions ? (
                  <KSpinner />
                ) : (
                  <ActivityList
                    activityGroups={this.state.activityGroups}
                    functionToShowSubject={(activity) => this.functionToShowSubject(activity)}
                    rerenderPageFunc={() => this.loadRequiredData()}
                    page={this}
                    contacts={contacts}
                  />
                )}
              </div>
            </div>
          </div>
        </Paper>
      </div>
    );
  }
}

ReferrerOrgPage.propTypes = {
  classes: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
  userInfo: state.userInfo,
  taskInfo: state.taskInfo,
  firms: state.firms,
  referrerOrgId: state.intendedInfo.intendedReferrerCompanyId,
});

const actionProducers = {
  updateFirm,
  updateVisibleFirm,
  clearTaskAccomplishmentInfo,
  setApiResponseInfo,
};

export default connect(mapStateToProps, actionProducers)(withStyles(referrerOrgPageStyles)(ReferrerOrgPage));
